import { useDroppable } from '@dnd-kit/core';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  FormControl,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { LabelGroup } from '@provision/entity-types';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { DynamicIcon, EllipsesMenu } from 'src/components';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { buildLabelsFilters, useLabel, useLabelGroups } from 'src/graphql';
import { ConditionInput } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { DraggableLabel, LabelForm } from './draggable-label';

export interface LabelGroupFormValues {
  editLabelGroup?: string;
}

export const LabelGroupForm = ({
  labelGroup,
  onSubmit,
  partnerId,
}: {
  labelGroup?: LabelGroup;
  onSubmit: () => void;
  partnerId?: number | null;
}) => {
  const formMethods = useForm<LabelGroupFormValues>({
    defaultValues: {
      editLabelGroup: labelGroup?.displayName ?? '',
    },
  });
  const selectedPartnerId = partnerId ?? useGlobalStore.use.selectedPartnerId();
  const { showSuccessAlert } = useNotifications();

  const { createLabelGroup, updateLabelGroup } = useLabelGroups({
    partnerId: selectedPartnerId,
  });

  const { control, handleSubmit } = formMethods;

  const onSubmitLabelForm = useCallback(
    async (data: LabelGroupFormValues) => {
      if (!data.editLabelGroup) {
        return;
      }

      if (labelGroup) {
        await updateLabelGroup({
          ...labelGroup,
          displayName: data.editLabelGroup || labelGroup.displayName,
        });
      } else {
        await createLabelGroup(
          {
            displayName: data.editLabelGroup,
          },
          selectedPartnerId,
        );
      }

      showSuccessAlert(labelGroup ? 'Group was updated' : 'Group was created');
      onSubmit();
    },
    [
      labelGroup,
      showSuccessAlert,
      onSubmit,
      updateLabelGroup,
      createLabelGroup,
      selectedPartnerId,
    ],
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmitLabelForm)}>
        <FormControl>
          <Controller
            name="editLabelGroup"
            control={control}
            render={({ field }) => (
              <TextField
                variant="outlined"
                size="small"
                label="Group name"
                autoFocus
                inputProps={{
                  onBlur: ({ target }) => {
                    const value = (target as HTMLInputElement).value;

                    if (!value || value === labelGroup?.displayName) {
                      onSubmit();
                    }
                  },
                }}
                {...field}
              />
            )}
          />
        </FormControl>
      </form>
    </FormProvider>
  );
};

const StyledAccordion = styled(Accordion, {
  name: 'StyledAccordion',
  shouldForwardProp: (prop) => prop !== 'isOver',
})<{
  isOver?: boolean;
}>(({ isOver, theme }) => ({
  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  borderRadius: theme.spacing(1),
  boxShadow: 'none',
  borderColor: isOver ? theme.palette.text.primary : undefined,

  '&.Mui-expanded': {
    marginBlock: 0,
  },
}));

const StyledAccordionSummary = styled(AccordionSummary, {
  name: 'StyledAccordionSummary',
})(({ theme }) => ({
  paddingInline: theme.spacing(1),
  minHeight: 'auto',
  flexDirection: 'row-reverse',

  '&.Mui-expanded': {
    minHeight: 'auto',
    paddingInline: theme.spacing(1),
  },

  '& .Mui-expanded': {
    marginBlock: theme.utils.important('0.75rem'),
  },

  '& .MuiAccordionSummary-content': {
    marginLeft: theme.utils.important(theme.spacing(1)),
  },
}));

export const DroppableGroup = ({
  group,
  labelsFilters,
  partnerId,
}: {
  group: LabelGroup;
  labelsFilters: ConditionInput[];
  partnerId?: number | null;
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: group.labelGroupId,
  });

  const [expand, setExpand] = useState(false);
  const toggleAcordion = () => {
    setExpand((prev) => !prev);
  };

  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const selectedPartnerId = partnerId ?? useGlobalStore.use.selectedPartnerId();

  const hasAccessToMenu =
    !isMultiTenancyMode || selectedPartnerId === PLATFORM_PARTNER_ID || group.partnerId !== 0;

  const [hasNewLabel, setHasNewLabel] = useState(false);

  const [isEditing, setIsEditing] = useState(false);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { deleteLabelGroup } = useLabelGroups({
    partnerId,
  });

  const { deleteLabel } = useLabel({
    filters: buildLabelsFilters({
      partnerId,
      extraFilters: labelsFilters,
    }),
  });

  const { showSuccessAlert } = useNotifications();

  const labels = useMemo(() => group.labels, [group.labels]);

  const menuOptions = useMemo(
    () => [
      {
        label: 'Edit',
        icon: 'pencil',
        iconLibrary: 'lucide' as const,
        onClick: (event?: MouseEvent<HTMLElement>) => {
          event?.stopPropagation();
          setIsMenuOpen(false);
          setIsEditing(true);
        },
      },
      {
        label: 'Delete',
        icon: 'trash-2',
        iconLibrary: 'lucide' as const,
        onClick: async (event?: MouseEvent<HTMLElement>) => {
          event?.stopPropagation();
          setIsMenuOpen(false);
          const deleteLabelsPromises = labels.map(async (label) => {
            await deleteLabel({
              ...label,
              partnerId: group.partnerId,
            });
          });

          await Promise.all(deleteLabelsPromises);

          await deleteLabelGroup({
            ...group,
            partnerId: group.partnerId,
          });
          showSuccessAlert('Group was deleted');
        },
      },
    ],
    [deleteLabel, deleteLabelGroup, group, labels, showSuccessAlert],
  );

  const createLabel = () => {
    setHasNewLabel(true);
  };

  return (
    <StyledAccordion expanded={expand} ref={setNodeRef} isOver={isOver}>
      <StyledAccordionSummary
        expandIcon={<DynamicIcon name="chevron-down" library="lucide" />}
        onClick={({ target }) => {
          if (!(target as HTMLElement).classList.contains('MuiBackdrop-invisible')) {
            toggleAcordion();
          }
        }}
        onMouseEnter={() => {
          setIsMenuOpen(true);
        }}
        onMouseLeave={() => {
          setIsMenuOpen(false);
        }}
      >
        <Stack spacing={1} direction="row">
          {isEditing ? (
            <LabelGroupForm onSubmit={() => setIsEditing(false)} labelGroup={group} />
          ) : null}
          {!isEditing && (
            <>
              <Typography
                variant="caption"
                fontWeight="500"
                sx={{
                  paddingBlock: 0.25,
                }}
              >
                {group.displayName}
              </Typography>
              {isMenuOpen && hasAccessToMenu ? (
                <EllipsesMenu
                  options={menuOptions}
                  icon="more-horizontal"
                  iconLibrary="lucide"
                />
              ) : null}
            </>
          )}
        </Stack>
      </StyledAccordionSummary>
      {!!labels.length || hasNewLabel ? (
        <AccordionDetails
          sx={{
            paddingBlock: 0,
            paddingInline: 1,
          }}
        >
          <Stack spacing={1}>
            {hasNewLabel ? (
              <LabelForm
                onSubmit={() => setHasNewLabel(false)}
                group={group}
                partnerId={partnerId}
                labelsFilters={labelsFilters}
              />
            ) : null}
            {labels.map((label) => (
              <DraggableLabel
                label={label}
                key={label.labelId}
                group={group}
                partnerId={partnerId}
                labelsFilters={labelsFilters}
              />
            ))}
          </Stack>
        </AccordionDetails>
      ) : null}
      <AccordionActions>
        <Button onClick={createLabel}>+ Label</Button>
      </AccordionActions>
    </StyledAccordion>
  );
};
